import {
  canRedo,
  canUndo,
  emptyHistory,
  nextRedoLabel,
  nextUndoLabel,
  describeRow,
  record,
  redo,
  undo,
  valuesBefore,
  type Edit,
  type History,
  type Operation,
  type Row,
} from "@roof-hawk/core";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { t } from "../i18n";
import { deleteRow, insertRow, updateRow } from "./queries";

/**
 * Undo and redo, wired to the database.
 *
 * The bookkeeping is in `packages/core/src/history.ts`, where it is tested.
 * This turns an `Operation` into a Supabase call, holds the stack for one
 * editor, and drives the toast at the bottom of the screen.
 *
 * Scope is deliberately one editor. Closing a work order forgets its history —
 * undoing an edit to a document you are no longer looking at, from a screen
 * that does not show it, would be worse than not offering it.
 */

export interface EditHistory {
  /**
   * Do something, and remember how to take it back.
   *
   * The action may hand back a corrected `Edit`, which is what an insert needs:
   * the id to record only exists once the row has been written.
   */
  run: (edit: Edit, action: () => Promise<Edit | void>) => Promise<void>;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
}

const HistoryContext = createContext<EditHistory | null>(null);

/**
 * The toast, and what it is offering to take back.
 *
 * One at a time. A stack of them covering the corner of the screen is how a
 * rep learns to ignore the corner of the screen.
 */
interface Toast {
  id: number;
  message: string;
  /** False once it has been used or the history moved on. */
  undoable: boolean;
}

const TOAST_SECONDS = 8;

export function EditHistoryProvider({
  onChanged,
  children,
}: {
  /** Called after every undo or redo, to reload whatever is on screen. */
  onChanged: () => Promise<void>;
  children: ReactNode;
}) {
  /*
   * The stack lives in a ref, with a counter to force the re-render.
   *
   * Not `useState`, because undo has to read the top of the stack and write the
   * rest of it in one go, and React may call a state updater more than once for
   * a single change. Reading inside the updater would then take two entries off
   * for one click; reading outside it races two fast clicks onto the same
   * entry. A ref is the single copy both problems come from not having.
   */
  const historyRef = useRef<History>(emptyHistory);
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const history = historyRef.current;
  const setHistory = useCallback((next: History) => {
    historyRef.current = next;
    bump();
  }, []);

  const [toast, setToast] = useState<Toast | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextToastId = useRef(0);

  const show = useCallback((message: string, undoable: boolean) => {
    if (timer.current) clearTimeout(timer.current);
    const id = ++nextToastId.current;
    setToast({ id, message, undoable });
    timer.current = setTimeout(
      () => setToast((current) => (current?.id === id ? null : current)),
      TOAST_SECONDS * 1000,
    );
  }, []);

  const run = useCallback(
    async (edit: Edit, action: () => Promise<Edit | void>) => {
      try {
        // Nothing is recorded when the write throws, so a failed edit cannot
        // leave an undo entry that would "restore" a row nobody changed.
        const corrected = await action();
        setHistory(record(historyRef.current, corrected ?? edit));
        await onChanged();
        setError(null);
        show(t(edit.label), true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [onChanged, setHistory, show],
  );

  const step = useCallback(
    async (direction: "undo" | "redo") => {
      const before = historyRef.current;
      const taken = direction === "undo" ? undo(before) : redo(before);
      if (!taken) return;

      // Moved before the write, so a second click while the first is in flight
      // takes the next entry rather than the same one twice.
      setHistory(taken.history);

      try {
        await apply(taken.operation);
        await onChanged();
        setError(null);
        show(
          direction === "undo"
            ? t("Undid: {label}", { label: t(taken.edit.label) })
            : t(taken.edit.label),
          // No Undo button on this toast: that would be a second way to undo
          // the same thing. The buttons in the editor are where undo lives.
          false,
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        // The edit was not taken back after all, so neither is the stack.
        setHistory(before);
      }
    },
    [onChanged, setHistory, show],
  );

  const value = useMemo<EditHistory>(
    () => ({
      run,
      undo: () => step("undo"),
      redo: () => step("redo"),
      canUndo: canUndo(history),
      canRedo: canRedo(history),
      undoLabel: nextUndoLabel(history),
      redoLabel: nextRedoLabel(history),
    }),
    [run, step, history],
  );

  return (
    <HistoryContext.Provider value={value}>
      {children}
      <EditToast
        toast={toast}
        error={error}
        canUndo={value.canUndo}
        onUndo={() => {
          setToast(null);
          void step("undo");
        }}
        onDismiss={() => {
          setToast(null);
          setError(null);
        }}
      />
    </HistoryContext.Provider>
  );
}

/**
 * The history for the editor this is inside.
 *
 * Outside a provider it hands back a working object that simply does the thing
 * and remembers nothing, so a table can be dropped anywhere without every
 * caller having to care.
 */
export function useEditHistory(): EditHistory {
  const value = useContext(HistoryContext);
  return value ?? PASSTHROUGH;
}

const PASSTHROUGH: EditHistory = {
  run: async (_edit, action) => {
    await action();
  },
  undo: async () => {},
  redo: async () => {},
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
};

async function apply(operation: Operation): Promise<void> {
  switch (operation.kind) {
    case "insert":
      // With its original id — see `undoOperation` in packages/core.
      await insertRow<Row>(operation.table, operation.row);
      return;
    case "update":
      await updateRow(operation.table, operation.id, operation.values);
      return;
    case "delete":
      await deleteRow(operation.table, operation.id);
      return;
  }
}

/** The bar bottom-left: what just happened, and a way to take it back. */
function EditToast({
  toast,
  error,
  canUndo: undoAvailable,
  onUndo,
  onDismiss,
}: {
  toast: Toast | null;
  error: string | null;
  canUndo: boolean;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  if (error) {
    return (
      <div className="edittoast bad" role="alert">
        <span>{error}</span>
        <button onClick={onDismiss}>{t("Dismiss")}</button>
      </div>
    );
  }
  if (!toast) return null;
  return (
    <div className="edittoast" role="status">
      <span>{toast.message}</span>
      {toast.undoable && undoAvailable && (
        <button onClick={onUndo}>{t("Undo")}</button>
      )}
    </div>
  );
}

/**
 * Add, change and remove rows in one table, all of it undoable.
 *
 * Every caller in the order editors goes through this rather than calling
 * `updateRow` directly, because an edit that is not recorded is an edit undo
 * silently skips — and a skipped edit makes undo worse than not having it.
 */
export function useTrackedRows(table: string): {
  patch: (row: Row, values: Row, label: string) => Promise<void>;
  remove: (row: Row) => Promise<void>;
  add: (values: Row, label?: string) => Promise<void>;
} {
  const history = useEditHistory();

  return useMemo(
    () => ({
      patch: (row, values, label) =>
        history.run(
          {
            kind: "update",
            table,
            id: String(row.id),
            before: valuesBefore(row, values),
            after: values,
            label,
          },
          () => updateRow(table, String(row.id), values),
        ),

      remove: (row) =>
        history.run(
          {
            kind: "delete",
            table,
            id: String(row.id),
            // The whole row, because putting it back means putting all of it
            // back — quantity, price, colour and the id it had.
            row,
            label: t("Removed {what}", { what: describeRow(row) }),
          },
          () => deleteRow(table, String(row.id)),
        ),

      // The id is a placeholder until the row exists; the action hands back
      // the entry with the real one.
      add: (values, label = t("Added a line")) =>
        history.run(
          { kind: "insert", table, id: "", row: values, label },
          async () => {
            const created = await insertRow<Row>(table, values);
            return {
              kind: "insert",
              table,
              id: String(created.id),
              row: created,
              label,
            };
          },
        ),
    }),
    [history, table],
  );
}

/**
 * The one ✕ on a line.
 *
 * No confirm dialog. It interrupted the ninety-nine deliberate deletes to catch
 * the one mistake, and did nothing about the mistakes people actually make —
 * see the note at the top of packages/core/src/history.ts.
 */
export function DeleteLineButton({
  row,
  table,
  title = t("Remove this line"),
}: {
  row: Row;
  table: string;
  title?: string;
}) {
  const rows = useTrackedRows(table);
  return (
    <button
      className="xbtn"
      title={title}
      onClick={() => void rows.remove(row)}
    >
      ✕
    </button>
  );
}

/** The two buttons, for the top of an editor. */
export function UndoRedoButtons() {
  const history = useEditHistory();
  return (
    <div className="undoredo">
      <button
        className="addrow ghost"
        disabled={!history.canUndo}
        title={
          history.undoLabel
            ? t("Undo: {label}", { label: t(history.undoLabel) })
            : t("Nothing to undo")
        }
        onClick={() => void history.undo()}
      >
        {t("↶ Undo")}
      </button>
      <button
        className="addrow ghost"
        disabled={!history.canRedo}
        title={
          history.redoLabel
            ? t("Redo: {label}", { label: t(history.redoLabel) })
            : t("Nothing to redo")
        }
        onClick={() => void history.redo()}
      >
        {t("↷ Redo")}
      </button>
    </div>
  );
}
